import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User, ArrowUpRight, CheckCircle2 } from "lucide-react";

export default function AuthModal({ open, mode = "login", onClose, onSwitchMode }) {
    const [success, setSuccess] = useState(false);
    const isLogin = mode === "login";

    const handleSubmit = (e) => {
        e.preventDefault();
        setSuccess(true);
        setTimeout(() => {
            setSuccess(false);
            onClose();
        }, 1600);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 z-[90] grid place-items-center bg-ink/50 p-4 backdrop-blur-sm"
                    data-testid="auth-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative grid w-full max-w-4xl grid-cols-1 overflow-hidden rounded-[24px] border border-ink/10 bg-white md:grid-cols-5"
                    >
                        {/* Visual side */}
                        <div className="relative hidden md:col-span-2 md:block">
                            <img
                                src="/assets/ktr.webp"
                                alt="KTR"
                                className="h-full w-full object-cover"
                            />
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                            <div className="absolute inset-6 flex flex-col justify-between text-white">
                                <div className="rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] backdrop-blur-md w-fit">
                                    Citizen Platform
                                </div>
                                <div>
                                    <div
                                        className="serif-italic text-[36px] leading-[0.9]"
                                        style={{ fontFamily: "Playfair Display" }}
                                    >
                                        Welcome
                                        <br />
                                        back.
                                    </div>
                                    <p className="mt-3 max-w-[220px] text-[12px] leading-relaxed text-white/80">
                                        A concept prototype — accounts are illustrative only.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Form side */}
                        <div className="relative md:col-span-3">
                            <button
                                onClick={onClose}
                                className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-ink/10 text-ink/70 hover:text-pink"
                                aria-label="Close"
                                data-testid="auth-close"
                            >
                                <X className="h-4 w-4" />
                            </button>

                            <div className="p-8 md:p-12">
                                <div className="eyebrow mb-3">{isLogin ? "Log in · Concept" : "Register · Concept"}</div>
                                <h3 className="font-display text-[30px] font-bold tracking-tight text-ink md:text-[38px]">
                                    {isLogin ? "Sign in to your citizen account." : "Create your citizen account."}
                                </h3>
                                <p className="mt-3 max-w-md text-[14px] leading-relaxed text-slate-500">
                                    {isLogin
                                        ? "Access your requests, ideas and event registrations from a single dashboard."
                                        : "Register once — file requests, submit ideas and track everything from one place."}
                                </p>

                                {success ? (
                                    <div
                                        className="mt-10 flex items-center gap-3 rounded-2xl border border-ink/10 bg-slate-50 p-5"
                                        data-testid="auth-success"
                                    >
                                        <div className="grid h-10 w-10 place-items-center rounded-full bg-pink/10">
                                            <CheckCircle2 className="h-5 w-5 text-pink" />
                                        </div>
                                        <div>
                                            <div className="font-display text-[15px] font-semibold text-ink">
                                                {isLogin ? "Signed in (demo)" : "Account created (demo)"}
                                            </div>
                                            <div className="text-[12px] text-slate-500">
                                                This is a concept — no real session is created.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-testid={isLogin ? "login-form" : "register-form"}>
                                        {!isLogin && (
                                            <div className="field">
                                                <label>Full name</label>
                                                <div className="relative">
                                                    <input required placeholder="Your full name" data-testid="auth-name" />
                                                    <User className="pointer-events-none absolute right-0 top-3 h-4 w-4 text-slate-400" />
                                                </div>
                                            </div>
                                        )}
                                        <div className="field">
                                            <label>Email</label>
                                            <div className="relative">
                                                <input required type="email" placeholder="you@domain.com" data-testid="auth-email" />
                                                <Mail className="pointer-events-none absolute right-0 top-3 h-4 w-4 text-slate-400" />
                                            </div>
                                        </div>
                                        <div className="field">
                                            <label>Password</label>
                                            <div className="relative">
                                                <input required type="password" placeholder="••••••••" data-testid="auth-password" />
                                                <Lock className="pointer-events-none absolute right-0 top-3 h-4 w-4 text-slate-400" />
                                            </div>
                                        </div>

                                        {isLogin && (
                                            <div className="flex items-center justify-between text-[12px] text-slate-500">
                                                <label className="inline-flex items-center gap-2">
                                                    <input type="checkbox" className="h-3.5 w-3.5 rounded border-ink/20 text-pink focus:ring-pink" />
                                                    Remember me
                                                </label>
                                                <button type="button" className="link-under-pink font-medium text-slate-700 hover:text-pink">
                                                    Forgot password?
                                                </button>
                                            </div>
                                        )}

                                        <button type="submit" className="btn-primary w-full justify-center" data-testid="auth-submit">
                                            {isLogin ? "Sign in" : "Create account"}
                                            <ArrowUpRight className="h-4 w-4" />
                                        </button>

                                        <div className="pt-2 text-center text-[13px] text-slate-500">
                                            {isLogin ? "New here?" : "Already a member?"}{" "}
                                            <button
                                                type="button"
                                                onClick={() => onSwitchMode(isLogin ? "register" : "login")}
                                                className="font-semibold text-ink hover:text-pink"
                                                data-testid="auth-switch"
                                            >
                                                {isLogin ? "Create an account" : "Sign in instead"}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                <div className="mt-10 border-t border-ink/10 pt-4 text-[11px] uppercase tracking-[0.24em] text-slate-400">
                                    Concept · No real authentication is performed
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
